import { Injectable, Inject } from '@angular/core';
import { Observable, throwError, of } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { BaseService } from './BaseService';
import { LookupTypeEnum } from '../Models/Enums/LookupTypeEnum';
import { ILookupBase, ILookupBaseWithValue } from '../Models/ILookupBase';
import { ILookupSite } from '../Models/ILookupSite';
import { ILookupGoal } from '../Models/ILookupGoal';
import { ILookupSchoolYear } from '../Models/ILookupSchoolYear';
import { ICALPADSMeetingDelay } from '../Models/ICALPADSMeetingDelay';
import { IReportPDF } from '../Models/IReportPDF';
import { IServiceProviderSearchParameters } from '../Models/IServiceProviderSearchParameters';
import { IStudentSearchParameters } from '../Models/IStudentSearchParameters';

@Injectable()
export class LookupService extends BaseService {

  _baseUrl: string = "";

  constructor(protected _http: HttpClient, @Inject('BASE_URL') baseUrl: string) { super(_http); this._baseUrl = baseUrl}

  getLookupItem(url: string): Observable<ILookupBase> {
    return this._http.get<ILookupBase>(url).map((response: ILookupBase) => response as ILookupBase)
      .catch(this.handleError);
  }

  getLookupArray(url: string): Observable<ILookupBase[]> {
    return this._http.get<ILookupBase[]>(url).map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  getLookupSiteList(url: string): Observable<ILookupBase[]> {
    return this._http.get<ILookupBase[]>(url).map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }
   
  getLookupSite(url: string): Observable<ILookupSite[]> {
    return this._http.get<ILookupSite[]>(url).map((response: ILookupSite[]) => response as ILookupSite[])
      .catch(this.handleError);
  }

  getLookupGoalList(addressedAreaId: number): Observable<ILookupGoal[]> {
    return this._http.get<ILookupGoal[]>(this._baseUrl + "api/Lookup/GetLookupGoalList/" + addressedAreaId)
      .map((response: ILookupGoal[]) => response as ILookupGoal[])
      .catch(this.handleError);
  }

  getRangeLookupSchoolYearList(): Observable<ILookupSchoolYear[]> {
    return this._http.get<ILookupSchoolYear[]>(this._baseUrl + "api/Lookup/GetRangeLookupSchoolYearList")
      .map((response: ILookupSchoolYear[]) => response as ILookupSchoolYear[])
      .catch(this.handleError);
  }

  getCALPADSMeetingDelayList(meetingTypeName: string): Observable<ICALPADSMeetingDelay[]> {
    return this._http.get<ICALPADSMeetingDelay[]>(this._baseUrl + "api/Lookup/GetCALPADSMeetingDelayList/" + meetingTypeName)
      .map((response: ICALPADSMeetingDelay[]) => response as ICALPADSMeetingDelay[])
      .catch(this.handleError);
  }

 //ADDED BY KEITH ISRAEL 20192509 API FOR PAU AND SITE CALLS
  getLookupPAUs(url: string): Observable<ILookupBase[]> {
    return this._http.get<ILookupBase[]>(url).map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }
  //ADDED BY KEITH ISRAEL 20192509 API FOR PAU AND SITE CALLS
  getLookupSites(url: string): Observable<ILookupBase[]> {
    return this._http.get<ILookupBase[]>(url).map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }
  //ADDED BY KEITH ISRAEL 20192410 API FOR ACADEMIC YEAR LOOKUP
  getLookupAcademicYearList(url: string): Observable<ILookupBase[]> {
    return this._http.get<ILookupBase[]>(url).map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  //ADDED BY KEITH ISRAEL 20192410 API FOR ATTENDANCE PERIOD LOOKUP
  getLookupAttendancePeriod(url: string): Observable<ILookupBase[]> {
    return this._http.get<ILookupBase[]>(url).map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  //ADDED BY KEITH ISRAEL 20192910 API FOR SERVICE PROVIDERS LOOKUP
  getLookupServiceProviders(url: string, param: IServiceProviderSearchParameters): Observable<ILookupBase[]> {
    return this._http.post<ILookupBase[]>(url, param)
      .map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  //ADDED BY KEITH ISRAEL 20193110 API FOR DIVISION LOOKUP
  getLookupDivision(url: string, param: IStudentSearchParameters): Observable<ILookupBase[]> {
    return this._http.post<ILookupBase[]>(url, param)
      .map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  //ADDED BY KEITH ISRAEL 20193110 API FOR STUDENT LOOKUP
  getLookupStudent(url: string, param: IStudentSearchParameters): Observable<ILookupBase[]> {
    return this._http.post<ILookupBase[]>(url, param)
      .map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  getLookupStudentByProvider(url: string, param: IStudentSearchParameters): Observable<ILookupBase[]> {
    return this._http.post<ILookupBase[]>(url, param)
      .map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  getIEPSummaryReportCriteriaForProvider(url: string): Observable<ILookupBase[]> {
    return this._http.get<ILookupBase[]>(url)
      .map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  getIEPSummaryReportCriteriaForSite(siteId: string): Observable<ILookupBase[]> {
    let url = `api/Lookup/GetIEPSummaryReportCriteriaForSite/${siteId}`;

    return this._http.get<ILookupBase[]>(url)
      .map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  getILookupBaseList(url: string): Observable<ILookupBase[]> {
    return this._http.get<ILookupBase[]>(url)
      .map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  //ADDED BY KEITH ISRAEL 20193110 API FOR SERVICE LOOKUP
  getLookupService(url: string, param: IStudentSearchParameters): Observable<ILookupBase[]> {
    return this._http.post<ILookupBase[]>(url, param)
      .map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  //ADDED BY KEITH ISRAEL 20191511 API FOR REPORTS LOOKUP
  getLookupReports(url: string): Observable<ILookupBase[]> {
    return this._http.get<ILookupBase[]>(url).map((response: ILookupBase[]) => response as ILookupBase[])
      .catch(this.handleError);
  }

  //ADDED BY KEITH ISRAEL 20191511 API FOR REPORTS LOOKUP
  getLookupBatch(url: string): Observable<ILookupBase[]> {
    return this._http.get<any>(url).map((response: any) => response as any)
      .catch(this.handleError);
  }

  getLookupBaseServiceDurationList(url: string): Observable<ILookupBaseWithValue[]> {
    return this._http.get<ILookupBaseWithValue[]>(url).map((response: ILookupBaseWithValue[]) => response as ILookupBaseWithValue[])
      .catch(this.handleError);
  }

  getLookupBaseServiceFrequencyList(url: string): Observable<ILookupBaseWithValue[]> {
    return this._http.get<ILookupBaseWithValue[]>(url).map((response: ILookupBaseWithValue[]) => response as ILookupBaseWithValue[])
      .catch(this.handleError);
  }
}

